/**
 * Token Refresh Manager
 * Handles proactive token refresh to maintain session for configured days
 * Prevents auto-logout on API errors
 * Configuration is centralized in appConfig.js
 */
import logger from '../utils/logger';
import appConfig from '../config/appConfig';
import { canAutoLoginReload, recordAutoLoginReload } from '../utils/autoLoginGuard';
import { hasOfficerndAutoLoginParams } from '../utils/officerndAutoLoginParams';

class TokenRefreshManager {
  constructor() {
    this.refreshTimer = null;
    this.monitorInterval = null;
    this.isRefreshing = false;
    this.refreshPromise = null;
    this.refreshAttempts = 0;
    this.maxRetries = appConfig.session.maxRetries;

    // Load configuration from centralized config file
    this.config = {
      refreshThreshold: appConfig.session.refreshThreshold,
      retryDelays: appConfig.session.retryDelays,
      tokenBuffer: appConfig.getTokenBufferMs(),
      checkInterval: appConfig.getCheckIntervalMs(),
      maxTokenAge: appConfig.getRefreshTokenLifetimeMs(),
      maxRetryOnError: appConfig.session.maxRetryOnError,
      errorRetryDelay: appConfig.getErrorRetryDelayMs(),
    };

    // Bind methods
    this.initialize = this.initialize.bind(this);
    this.scheduleTokenRefresh = this.scheduleTokenRefresh.bind(this);
    this.refreshToken = this.refreshToken.bind(this);
    this.handleStorageChange = this.handleStorageChange.bind(this);
    this.handleVisibilityChange = this.handleVisibilityChange.bind(this);
    this.handleOnline = this.handleOnline.bind(this);

    // Initialize only if not in test environment
    if (typeof process === 'undefined' || process.env.NODE_ENV !== 'test') {
      // Start monitoring
      this.initialize();
    }
  }

  /**
   * Initialize the token refresh manager
   */
  initialize() {
    const accessToken = localStorage.getItem('access_token');
    const refreshToken = localStorage.getItem('refresh_token');
    const rollingDays = appConfig.session.rollingSessionDays;
    logger.info('TokenRefreshManager', `Initializing token refresh manager for rolling ${rollingDays}-day sessions`);

    if (accessToken && refreshToken) {
      // Schedule the first refresh based on access token expiry
      this.scheduleTokenRefresh(accessToken);

      // Start periodic monitoring for activity-based refresh
      this.startTokenMonitor();

      // Listen for token updates in other tabs (cross-tab synchronization)
      window.addEventListener('storage', this.handleStorageChange);

      // Listen for visibility change to refresh when tab becomes active
      document.addEventListener('visibilitychange', this.handleVisibilityChange);

      // Listen for online event to refresh after network recovery
      window.addEventListener('online', this.handleOnline);
    } else {
      logger.warn('TokenRefreshManager', 'No tokens found in localStorage - skipping initialization');
    }
  }

  /**
   * Parse JWT token without verification (frontend)
   */
  parseToken(token) {
    try {
      const base64Url = token.split('.')[1];
      const base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/');
      const jsonPayload = decodeURIComponent(
        atob(base64)
          .split('')
          .map(c => '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2))
          .join('')
      );
      return JSON.parse(jsonPayload);
    } catch (error) {
      logger.error('TokenRefreshManager', 'Failed to parse token', { error });
      return null;
    }
  }

  /**
   * Get token expiration time in milliseconds
   */
  getTokenExpiry(token) {
    const payload = this.parseToken(token);
    if (payload && payload.exp) {
      return payload.exp * 1000; // Convert to milliseconds
    }
    return null;
  }

  /**
   * Check if token should be refreshed
   */
  shouldRefresh(token) {
    const expiry = this.getTokenExpiry(token);
    if (!expiry) return true; // If can't parse, refresh to be safe

    const now = Date.now();
    const timeUntilExpiry = expiry - now;

    // If within buffer time, refresh immediately
    if (timeUntilExpiry <= this.config.tokenBuffer) {
      logger.warn('TokenRefreshManager', 'Token within buffer time, refreshing immediately', {
        timeUntilExpiry: Math.floor(timeUntilExpiry / 1000) + 's'
      });
      return true;
    }

    // Check if we've passed the refresh threshold
    const payload = this.parseToken(token);
    if (payload && payload.iat) {
      const issuedAt = payload.iat * 1000;
      const totalLifetime = expiry - issuedAt;
      const elapsed = now - issuedAt;
      const percentageElapsed = elapsed / totalLifetime;

      if (percentageElapsed >= this.config.refreshThreshold) {
        logger.info('TokenRefreshManager', 'Token past refresh threshold', {
          percentageElapsed: Math.floor(percentageElapsed * 100) + '%'
        });
        return true;
      }
    }

    return false;
  }

  /**
   * Schedule proactive token refresh
   */
  scheduleTokenRefresh(token) {
    // Clear existing timer
    if (this.refreshTimer) {
      clearTimeout(this.refreshTimer);
    }

    const expiry = this.getTokenExpiry(token);
    if (!expiry) {
      logger.error('TokenRefreshManager', 'Cannot schedule refresh - invalid token');
      return;
    }

    const payload = this.parseToken(token);
    if (!payload || !payload.iat) return;

    const issuedAt = payload.iat * 1000;
    const totalLifetime = expiry - issuedAt;
    const refreshAt = issuedAt + (totalLifetime * this.config.refreshThreshold);
    const now = Date.now();
    const delay = Math.max(0, refreshAt - now);

    logger.info('TokenRefreshManager', 'Scheduling token refresh', {
      refreshIn: Math.floor(delay / 1000) + 's',
      expiresIn: Math.floor((expiry - now) / 1000) + 's'
    });

    this.refreshTimer = setTimeout(() => {
      this.refreshToken();
    }, delay);
  }

  /**
   * Start periodic token monitor
   */
  startTokenMonitor() {
    // Clear existing interval if any (prevent duplicates)
    if (this.monitorInterval) {
      clearInterval(this.monitorInterval);
    }

    // Check token status periodically and store interval ID
    this.monitorInterval = setInterval(() => {
      this.checkAndRefreshIfNeeded();
    }, this.config.checkInterval);
  }

  /**
   * Check and refresh token if needed
   */
  async checkAndRefreshIfNeeded() {
    const accessToken = localStorage.getItem('access_token');
    const refreshToken = localStorage.getItem('refresh_token');

    if (!accessToken || !refreshToken) {
      logger.warn('TokenRefreshManager', 'No tokens found, skipping refresh check');
      return;
    }

    // Check refresh token validity
    const refreshExpiry = this.getTokenExpiry(refreshToken);
    if (refreshExpiry && refreshExpiry < Date.now()) {
      logger.error('TokenRefreshManager', 'Refresh token expired - no refresh within 7-day rolling window');
      this.handleExpiredSession();
      return;
    }

    // Check if access token needs refresh
    if (this.shouldRefresh(accessToken)) {
      await this.refreshToken();
    }
  }

  /**
   * Refresh the access token with error resilience
   */
  async refreshToken(retryCount = 0) {
    // Prevent concurrent refreshes
    if (this.isRefreshing) {
      logger.info('TokenRefreshManager', 'Refresh already in progress, waiting...');
      return this.refreshPromise;
    }

    const refreshToken = localStorage.getItem('refresh_token');
    if (!refreshToken) {
      logger.error('TokenRefreshManager', 'No refresh token found');
      this.handleExpiredSession();
      return null;
    }

    this.isRefreshing = true;
    logger.info('TokenRefreshManager', `Refreshing token (attempt ${retryCount + 1}/${this.maxRetries + 1})`);

    this.refreshPromise = (async () => {
      try {
        // Import dynamically to avoid circular dependency
        const { default: axios } = await import('axios');
        const { default: environmentConfig } = await import('../utils/environmentConfig');

        // Send old access token in Authorization header so backend can find
        // the exact session for this device (multi-device safe refresh)
        const oldAccessToken = localStorage.getItem('access_token');
        const headers = {};
        if (oldAccessToken) {
          headers.Authorization = `Bearer ${oldAccessToken}`;
        }

        const response = await axios.post(
          `${environmentConfig.apiUrl}/auth/refresh`,
          { refresh_token: refreshToken },
          {
            timeout: 10000,
            withCredentials: false,
            headers
          }
        );

        const { access_token, refresh_token: newRefreshToken } = response.data;

        // Update tokens in localStorage
        localStorage.setItem('access_token', access_token);
        if (newRefreshToken) {
          localStorage.setItem('refresh_token', newRefreshToken);
        }

        logger.success('TokenRefreshManager', 'Token refreshed successfully');

        // Emit custom event for other parts of the app
        window.dispatchEvent(new CustomEvent('tokenRefreshed', {
          detail: {
            accessToken: access_token,
            refreshToken: newRefreshToken
          }
        }));

        // Schedule next refresh based on new token expiry
        this.scheduleTokenRefresh(access_token);

        this.isRefreshing = false;
        this.refreshAttempts = 0;
        return response.data;
      } catch (error) {
        logger.error('TokenRefreshManager', `Token refresh failed (attempt ${retryCount + 1})`, {
          error: error.message,
          status: error.response?.status,
          isNetworkError: !error.response
        });

        // Determine if this is a permanent or temporary failure
        const isPermanentFailure = error.response?.status === 401 ||
                                   error.response?.status === 403;

        const isNetworkError = !error.response ||
                               error.code === 'ECONNABORTED' ||
                               error.code === 'ERR_NETWORK';

        const isServerError = error.response?.status >= 500;
        const isRateLimited = error.response?.status === 429;

        // Retry with exponential backoff for temporary failures
        if (retryCount < this.maxRetries && !isPermanentFailure) {
          const delay = this.config.retryDelays[retryCount] || 5000;
          logger.info('TokenRefreshManager', `Retrying token refresh in ${delay}ms (temporary error)`);

          await new Promise(resolve => setTimeout(resolve, delay));
          this.isRefreshing = false;
          return this.refreshToken(retryCount + 1);
        }

        // Max retries exceeded
        this.isRefreshing = false;

        // Only handle expired session for permanent auth failures
        if (isPermanentFailure) {
          // Check if refresh token is actually expired
          const refreshToken = localStorage.getItem('refresh_token');
          const refreshExpiry = refreshToken ? this.getTokenExpiry(refreshToken) : null;

          if (refreshExpiry && refreshExpiry < Date.now()) {
            logger.error('TokenRefreshManager', 'Refresh token expired - no activity for 7 days');
            this.handleExpiredSession();
          } else {
            logger.error('TokenRefreshManager', 'Auth failure but refresh token still valid - will retry');
            // Schedule another attempt after delay
            setTimeout(() => {
              this.checkAndRefreshIfNeeded();
            }, this.config.errorRetryDelay);
          }
        } else if (isNetworkError || isServerError || isRateLimited) {
          // Temporary failure - DO NOT LOGOUT
          logger.warn('TokenRefreshManager', `Temporary failure (${isNetworkError ? 'network' : isServerError ? 'server' : 'rate-limited'}), will retry in ${this.config.errorRetryDelay / 1000}s`);

          // Schedule retry after delay
          setTimeout(() => {
            this.checkAndRefreshIfNeeded();
          }, this.config.errorRetryDelay);

          // Don't throw error - just log and continue
          return null;
        }

        // Only throw for unrecoverable errors
        if (isPermanentFailure) {
          throw error;
        }

        return null;
      }
    })();

    return this.refreshPromise;
  }

  /**
   * Handle expired session (after 7 days of inactivity)
   */
  handleExpiredSession() {
    logger.warn('TokenRefreshManager', 'Session expired after 7 days of inactivity, clearing tokens');

    // Don't auto-logout immediately - let the user know
    const event = new CustomEvent('sessionExpired', {
      detail: { reason: 'Session expired due to inactivity. Rolling 7-day sessions require at least one refresh within 7 days.' }
    });
    window.dispatchEvent(event);

    // Clear tokens after a delay to allow the app to show a message
    setTimeout(() => {
      localStorage.removeItem('access_token');
      localStorage.removeItem('refresh_token');

      // Auto-login mode: URL or sessionStorage has the OfficeRND params →
      // reload to re-trigger auto-login. The URL may have been scrubbed
      // for security; sessionStorage holds the per-tab mirror.
      if (hasOfficerndAutoLoginParams()) {
        if (canAutoLoginReload()) {
          recordAutoLoginReload();
          window.location.reload();
        } else {
          logger.error('TokenRefreshManager', 'Auto-login reload limit reached — stopping to prevent loop');
        }
        return;
      }

      // Normal mode: redirect to login page
      if (!window.location.pathname.includes('/login')) {
        window.location.href = '/login?session=expired';
      }
    }, 2000);
  }

  /**
   * Handle storage changes from other tabs
   */
  handleStorageChange(event) {
    if (event.key === 'access_token' && event.newValue) {
      logger.info('TokenRefreshManager', 'Token updated in another tab, rescheduling refresh');

      // Cancel current timer
      if (this.refreshTimer) {
        clearTimeout(this.refreshTimer);
      }

      // Schedule refresh for new token
      this.scheduleTokenRefresh(event.newValue);
    } else if (event.key === 'access_token' && !event.newValue) {
      // Token removed - user logged out in another tab
      logger.info('TokenRefreshManager', 'User logged out in another tab');

      if (this.refreshTimer) {
        clearTimeout(this.refreshTimer);
      }

      // Clear local storage too
      localStorage.removeItem('access_token');
      localStorage.removeItem('refresh_token');

      // Redirect to login
      if (!window.location.pathname.includes('/login')) {
        window.location.href = '/login';
      }
    }
  }

  /**
   * Handle visibility change event
   */
  handleVisibilityChange() {
    const accessToken = localStorage.getItem('access_token');
    if (!document.hidden && accessToken) {
      this.checkAndRefreshIfNeeded();
    }
  }

  /**
   * Handle online event
   */
  handleOnline() {
    logger.info('TokenRefreshManager', 'Network connection restored, checking token');
    this.checkAndRefreshIfNeeded();
  }

  /**
   * Cleanup - Clear all timers and event listeners
   */
  destroy() {
    // Clear refresh timer
    if (this.refreshTimer) {
      clearTimeout(this.refreshTimer);
      this.refreshTimer = null;
    }

    // Clear monitor interval
    if (this.monitorInterval) {
      clearInterval(this.monitorInterval);
      this.monitorInterval = null;
    }

    // Remove event listeners
    window.removeEventListener('storage', this.handleStorageChange);
    document.removeEventListener('visibilitychange', this.handleVisibilityChange);
    window.removeEventListener('online', this.handleOnline);
  }
}

// Create singleton instance
let tokenRefreshManager = null;

export const getTokenRefreshManager = () => {
  if (!tokenRefreshManager) {
    tokenRefreshManager = new TokenRefreshManager();
  }
  return tokenRefreshManager;
};

// Auto-initialize on import (except in tests)
if (typeof window !== 'undefined' &&
    (typeof process === 'undefined' || process.env.NODE_ENV !== 'test')) {
  getTokenRefreshManager();
}

export default TokenRefreshManager;